import { useTranslation } from "react-i18next"
import { livisAlert } from "./alert"
import { getLivisUserInfo, getLivistheme } from "./storage"

/**
 * Checks if a string contains at least one uppercase letter.
 * @param str - Input string.
 * @returns True if contains uppercase, else false.
 */
export const checkUppercase = (str: string) => {
  return /[A-Z]/.test(str)
}

/**
 * Checks if a string contains at least one lowercase letter.
 * @param str - Input string.
 * @returns True if contains lowercase, else false.
 */
export const checkLowercase = (str: string) => {
  return /[a-z]/.test(str)
}

/**
 * Checks if a string contains at least one number.
 * @param str - Input string.
 * @returns True if contains number, else false.
 */
export const checkNumber = (str: string) => {
  return /[0-9]/.test(str)
}

/**
 * Checks if a string contains at least one special character.
 * @param str - Input string.
 * @returns True if contains special character, else false.
 */
export const checkSpecialCharacter = (str: string) => {
  return /[@$!%*#?&]/.test(str)
}

/**
 * Removes underscores from a string.
 * @param str - Input string.
 * @returns String without underscores.
 */
export const removeUnderscores = (str: any) => {
  if (!str?.length) return str
  return str.replace(/_/g, " ")
}

/**
 * Capitalizes the first letter of each word in a string.
 * @param str - Input string.
 * @returns Formatted string.
 */
export const formatString = (str: string) => {
  if (!str?.length) return str

  const separateWord = str?.split(" ")
  separateWord?.forEach((word, index) => {
    separateWord[index] = word?.charAt(0)?.toUpperCase() + word?.substring(1)
  })
  return separateWord?.join(" ")
}

/**
 * Capitalizes the first character of a string.
 * @param str - Input string.
 * @returns String with first character capitalized.
 */
export const formatFirstCharacter = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

/**
 * Converts bytes to GB and formats as a string.
 * @param data - Size in bytes.
 * @returns Formatted size string in GB.
 */
export const formatSize = (data: number) => {
  const data_size = (data / 1024).toFixed(1)
  return data_size + " GB"
}

/**
 * Formats image size from bytes to KB, MB, or GB.
 * @param data - Size in bytes.
 * @returns Formatted size string.
 */
export const formatImageSize = (data: number) => {
  if (data >= 1073741824) {
    const data_size = Math.floor(data / 1073741824)
    return data_size.toFixed(2) + " GB"
  } else if (data >= 1048576) {
    const data_size = Math.floor(data / 1048576)
    return data_size.toFixed(2) + " MB"
  } else if (data >= 1024) {
    const data_size = Math.floor(data / 1024)
    return data_size.toFixed(2) + " KB"
  } else {
    return data?.toFixed(2) + " Bytes"
  }
}

/**
 * Formats a count value for display.
 * @param data - Count value.
 * @returns Formatted count as string.
 */
export const formatCount = (data: any): string => {
  if (data === 0) {
    return "0"
  } else if (data == null) {
    return "--"
  } else {
    const formattedValue = data.toString()
    return formattedValue.length === 1
      ? formattedValue
      : formattedValue.padStart(2, "0")
  }
}

/**
 * Shows a success alert for HTTP status codes 2xx.
 * @param msg - Message to display.
 * @param status - HTTP status code.
 */
export const showAlert = (msg: any, status: number) => {
  //console.log(msg, status);
  switch (true) {
    case status >= 200 && status < 300:
      livisAlert(msg, "success")
      break
  }
}

/**
 * Formats seconds as HH:MM:SS.
 * @param seconds - Number of seconds.
 * @returns Formatted time string.
 */
export const formatSecond = (seconds: any) => {
  const formatSeconds = new Date(seconds * 1000)?.toISOString()?.slice(11, 19)
  return formatSeconds
}

/**
 * Shows an error alert based on HTTP response status.
 * @param response - HTTP response object.
 */
export const alert_error = (response: any) => {
  // const { t } = useTranslation();
  switch (response?.status) {
    case 500:
      livisAlert(`Internal server error`, "error")
      break
    default:
      livisAlert(response?.data?.message, "error")
  }
}

/**
 * Formats seconds as "HHh MMm SSs".
 * @param second - Number of seconds.
 * @returns Formatted ETA string.
 */
export const formatETA = (second: number) => {
  const safeSeconds = Math.max(0, Math.floor(second));

  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const secs = safeSeconds % 60;

  const pad = (n: number) => String(n).padStart(2, "0");

  return `${pad(hours)}h ${pad(minutes)}m ${pad(secs)}s`;
};

/**
 * Converts a local date string to UTC format (YYYY-MM-DD).
 * Handles time zone differences for consistent UTC dates.
 * @param dateString - Date string in format YYYY-MM-DD or YYYY-MM-DD HH:MM:SS
 * @returns UTC formatted date string (YYYY-MM-DD)
 */
export const convertToUTCDate = (dateString: string): string => {
  if (!dateString) return "";

  try {
    // Parse the input date string
    let date: Date;

    // Check if the date string already includes time
    if (dateString.includes(":")) {
      date = new Date(dateString);
    } else {
      // If date only (YYYY-MM-DD), we need to handle it properly
      // Create a date object in the local timezone
      const [year, month, day] = dateString.split('-').map(num => parseInt(num, 10));

      // JavaScript months are 0-indexed (0-11), so subtract 1 from month
      date = new Date(year, month - 1, day);
    }

    // Check if the date is valid
    if (isNaN(date.getTime())) {
      console.error("Invalid date:", dateString);
      return "";
    }

    // Convert to UTC
    const utcYear = date.getUTCFullYear();
    const utcMonth = String(date.getUTCMonth() + 1).padStart(2, "0");
    const utcDay = String(date.getUTCDate()).padStart(2, "0");

    return `${utcYear}-${utcMonth}-${utcDay}`;
  } catch (error) {
    console.error("Error converting to UTC date:", error);
    return "";
  }
};

/**
 * Converts a UTC date string to local date format.
 * @param utcDateString - UTC date string.
 * @param format - Optional formatting parameter.
 * @returns Formatted local date string.
 */
export const convertUTCDateToLocalDate = (utcDateString: string, format?: any): string => {
  if (!utcDateString) return "";

  let dateObj: Date;

  try {
    // Create a Date object from the UTC string
    if (format) {
      dateObj = new Date(utcDateString);
    } else {
      // Ensure the string is treated as UTC by appending 'Z' if not present
      dateObj = new Date(utcDateString.endsWith('Z') ? utcDateString : `${utcDateString}Z`);
    }

    // Check if the date is valid
    if (isNaN(dateObj.getTime())) {
      console.error("Invalid UTC date:", utcDateString);
      return "";
    }

    // Format the date in local time
    const localTimeString = dateObj.toLocaleString(undefined, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });

    // Fix the 00 hour issue by replacing it with 12
    return localTimeString.replace(/\b00:/, "12:");
  } catch (error) {
    console.error("Error converting UTC date to local date:", error);
    return "";
  }
};

/**
 * Converts a UTC date string to local date and time with seconds.
 * @param utcDateString - UTC date string.
 * @param format - Optional formatting parameter.
 * @returns Formatted local date and time string.
 */
export const convertToLocalDateTimeWithSecs = (utcDateString: string, format?: any): string => {
  if (!utcDateString) return "";

  let dateObj: Date;

  try {
    // Create a Date object from the UTC string
    if (format) {
      dateObj = new Date(utcDateString);
    } else {
      // Ensure the string is treated as UTC by appending 'Z' if not present
      dateObj = new Date(utcDateString.endsWith('Z') ? utcDateString : `${utcDateString}Z`);
    }

    // Check if the date is valid
    if (isNaN(dateObj.getTime())) {
      console.error("Invalid UTC date:", utcDateString);
      return "";
    }

    // Format the date in local time with seconds
    const localTimeString = dateObj.toLocaleString(undefined, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });

    // Fix the 00 hour issue by replacing it with 12
    return localTimeString.replace(/\b00:/, "12:");
  } catch (error) {
    console.error("Error converting UTC date to local date:", error);
    return "";
  }
};

/**
 * Calculates the shift (Morning, Afternoon, Evening, Night) based on the hour of a date string.
 * @param dateString - Date string.
 * @returns Shift name.
 */
export const calculateShift = (dateString: any) => {
  // Parse the input date string
  const date = new Date(dateString + "Z");
  // Get the hour from the date
  const hour = date.getHours();

  // Define shift boundaries
  const morningStart = 6;
  const morningEnd = 12;
  const afternoonStart = 12;
  const afternoonEnd = 16;
  const eveningStart = 16;
  const eveningEnd = 20;

  // Determine the shift based on the hour
  if (hour >= morningStart && hour < morningEnd) {
    return "Morning shift";
  } else if (hour >= afternoonStart && hour < afternoonEnd) {
    return "Afternoon shift";
  } else if (hour >= eveningStart && hour < eveningEnd) {
    return "Evening shift";
  } else {
    return "Night shift";
  }
};

/**
 * Formats a date string as "DD Mon YYYY".
 * @param date - Date string in "DD/MM/YYYY" format.
 * @returns Formatted date string.
 */
export const formatMonth = (date: string) => {
  const d_array = date?.split("/");
  switch (d_array[1]) {
    case "01":
      return d_array[0] + " Jan " + d_array[2];
    case "02":
      return d_array[0] + " Feb " + d_array[2];
    case "03":
      return d_array[0] + " Mar " + d_array[2];
    case "04":
      return d_array[0] + " Apr " + d_array[2];
    case "05":
      return d_array[0] + " May " + d_array[2];
    case "06":
      return d_array[0] + " Jun " + d_array[2];
    case "07":
      return d_array[0] + " Jul " + d_array[2];
    case "08":
      return d_array[0] + " Aug " + d_array[2];
    case "09":
      return d_array[0] + " Sep " + d_array[2];
    case "10":
      return d_array[0] + " Oct " + d_array[2];
    case "11":
      return d_array[0] + " Nov " + d_array[2];
    case "12":
      return d_array[0] + " Dec " + d_array[2];
  }
};

/**
 * Formats a date string as "MM Mon YYYY".
 * @param date - Date string in "MM/DD/YYYY" format.
 * @returns Formatted date string.
 */
export const formatMonthmmdd = (date: string) => {
  const d_array = date?.split("/");
  switch (d_array[0]) {
    case "01":
      return d_array[0] + " Jan " + d_array[2];
    case "02":
      return d_array[0] + " Feb " + d_array[2];
    case "03":
      return d_array[0] + " Mar " + d_array[2];
    case "04":
      return d_array[0] + " Apr " + d_array[2];
    case "05":
      return d_array[0] + " May " + d_array[2];
    case "06":
      return d_array[0] + " Jun " + d_array[2];
    case "07":
      return d_array[0] + " Jul " + d_array[2];
    case "08":
      return d_array[0] + " Aug " + d_array[2];
    case "09":
      return d_array[0] + " Sep " + d_array[2];
    case "10":
      return d_array[0] + " Oct " + d_array[2];
    case "11":
      return d_array[0] + " Nov " + d_array[2];
    case "12":
      return d_array[0] + " Dec " + d_array[2];
  }
};

/**
 * Formats a datetime string to "DD Mon YYYY" format (date only, no time).
 * Handles various date formats including "DD Mon YYYY, HH:MM:SS".
 * @param dateTime - Date string with or without time.
 * @returns Formatted date string without time.
 */
export const formatDateOnly = (dateTime: string) => {
  if (!dateTime) return '';

  try {
    // Handle different date formats
    let dateObj: Date;
    
    // If it's already a Date object or a valid date string
    if (dateTime.includes(',')) {
      // Format like "10 Oct 2025, 08:26:39" - remove time part
      const datePart = dateTime.split(',')[0].trim();
      dateObj = new Date(datePart);
    } else {
      // Other date formats
      dateObj = new Date(dateTime);
    }

    // Check if date is valid
    if (isNaN(dateObj.getTime())) {
      return dateTime; // Return original if can't parse
    }

    // Format as "DD Mon YYYY"
    const day = dateObj.getDate().toString().padStart(2, '0');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                       'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[dateObj.getMonth()];
    const year = dateObj.getFullYear();

    return `${day} ${month} ${year}`;
  } catch (error) {
    // Return original string if parsing fails
    return dateTime;
  }
};

/**
 * Checks if the user has permission to access a page or action.
 * @param permission_id - Permission identifier.
 * @returns True if user has access, else false.
 */
export const doesUserHasAccess = (permission_id: any) => {
  // return true
  const user_info_string: any = getLivisUserInfo();
  const user_info = JSON.parse(user_info_string);
  const isSuperuser = user_info?.is_superuser;
  if (isSuperuser) return true;
  const permissions = user_info?.user_permissions;
  const hasAcces = permissions?.includes(permission_id);
  return hasAcces;
};

/**
 * Gets the current user's email.
 * @returns User email string.
 */
export const currentUserName = () => {
  const user_info_string: any = getLivisUserInfo();
  const user_info = JSON.parse(user_info_string);
  return user_info?.email;
}

/**
 * Hides console logs in production environment.
 */
export const hideConsoleLogs = () => {
  if (process.env.NODE_ENV === "production") {
    console.log = () => {
      return;
    };
    // console.error = () => { return };
    console.warn = () => {
      return;
    };
    console.info = () => {
      return;
    };
  }
};

/**
 * Checks if a URL is a valid image URL (http(s) or data URL).
 * @param url - Image URL string.
 * @returns True if valid image URL, else false.
 */
export function isValidImageUrl(url: string) {
  if (!url || typeof url !== 'string') {
    return false;
  }

  const imageExtensions = '(jpeg|jpg|png|gif|bmp|webp|svg|mpo|tif|tiff|heic|heif|avif)';

  // Matches http or https, any hostname or IP, optional port, and valid image path under /data_drive/ (optional presigned query)
  const livisUrlPattern = new RegExp(
    `^https?:\\/\\/[\\w.-]+(?::\\d+)?\\/data_drive(?:\\/[^/]+)*\\/[^/]+\\.${imageExtensions}(?:\\?.*)?$`,
    'i'
  );

  // DigitalOcean Spaces CDN URL pattern (optional presigned query)
  const digitalOceanSpacesCdnPattern = new RegExp(
    `^https?:\\/\\/[\\w.-]+\\.cdn\\.digitaloceanspaces\\.com(?:\\/[\\w.-]+)*\\/[^/]+\\.${imageExtensions}(?:\\?.*)?$`,
    'i'
  );

  // DigitalOcean Spaces region endpoint presigned URL (e.g. nyc3.digitaloceanspaces.com/bucket/path/file.jpg?X-Amz-...)
  const digitalOceanSpacesRegionPattern = new RegExp(
    `^https?:\\/\\/[a-z0-9-]+\\.digitaloceanspaces\\.com(?:\\/[^/]+)*\\/[^/]+\\.${imageExtensions}(?:\\?.*)?$`,
    'i'
  );

  // Storj gateway URL (e.g. gateway.storjshare.io/.../file.jpg?X-Amz-...)
  const storjGatewayPattern = new RegExp(
    `^https?:\\/\\/gateway\\.storjshare\\.io\\/(?:[^?/]+\\/)*[^/]+\\.${imageExtensions}(?:\\?.*)?$`,
    'i'
  );

  // Storj link URL (e.g. link.storjshare.io/s/xxx/livis/.../file.jpg, optional presigned query)
  const storjLinkPattern = new RegExp(
    `^https?:\\/\\/link\\.storjshare\\.io\\/(?:[^/]+\\/)+[^/]+\\.${imageExtensions}(?:\\?.*)?$`,
    'i'
  );

  // AWS S3 URL (e.g. bucket.s3.amazonaws.com/path/to/file.jpg?AWSAccessKeyId=...&Signature=...&Expires=...)
  const awsS3Pattern = new RegExp(
    `^https?:\\/\\/[\\w.-]+\\.s3(?:\\.[a-z0-9-]+)?\\.amazonaws\\.com(?:\\/[^/]+)*\\/[^/]+\\.${imageExtensions}(?:\\?.*)?$`,
    'i'
  );

  // Data URL pattern
  const dataUrlPattern = new RegExp(
    `^data:image/${imageExtensions};base64,[A-Za-z0-9+/]+=*$`,
    'i'
  );

  return (
    livisUrlPattern.test(url) ||
    digitalOceanSpacesCdnPattern.test(url) ||
    digitalOceanSpacesRegionPattern.test(url) ||
    storjGatewayPattern.test(url) ||
    storjLinkPattern.test(url) ||
    awsS3Pattern.test(url) ||
    dataUrlPattern.test(url)
  );
}

/**
 * Calculates the shift (Morning, Afternoon, Evening, Night) with translation.
 * @param dateString - Date string.
 * @param t - Translation function.
 * @returns Translated shift name.
 */
export const shiftCalculator = (dateString: any, t: any) => {
  // Parse the input date string
  const date = new Date(dateString + "Z")
  // Get the hour from the date
  const hour = date.getHours()

  // Define shift boundaries
  const morningStart = 6
  const morningEnd = 12
  const afternoonStart = 12
  const afternoonEnd = 16
  const eveningStart = 16
  const eveningEnd = 20

  // Determine the shift based on the hour
  if (hour >= morningStart && hour < morningEnd) {
    return t("Morning shift")
  } else if (hour >= afternoonStart && hour < afternoonEnd) {
    return t("Afternoon shift")
  } else if (hour >= eveningStart && hour < eveningEnd) {
    return t("Evening shift")
  } else {
    return t("Night shift")
  }
}

/**
 * Converts milliseconds to "HH:MM:SS hrs" format.
 * @param ms - Milliseconds.
 * @returns Formatted time string.
 */
export const formatMsToHr = (ms: number): string => {
  const totalSeconds = Math.floor(ms / 1000)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  const padded = (n: number) => n.toString().padStart(2, '0')

  return `${padded(hours)}:${padded(minutes)}:${padded(seconds)} hrs`
}

/**
 * Extracts the domain from an email address.
 * @param email - Email string.
 * @returns Domain string.
 */
export const getUserDomain = (email: string): string => {
  if (!email || typeof email !== "string") return "";
  const parts = email.split("@");
  return parts.length === 2 ? parts[1] : "";
};

/**
 * Checks if the current user is a superuser.
 * @returns True if superuser, else false.
 */
export const isSuperuser = () => {
  const user_info_string: any = getLivisUserInfo();
  const user_info = JSON.parse(user_info_string);
  return user_info?.is_superuser || false;
};

/**
 * Formats seconds as a phrase "Xh Ym Zs".
 * @param seconds - Number of seconds.
 * @returns Formatted phrase.
 */
export function formatSecondsToPhrase(seconds: any): string {
  const totalSeconds = Math.floor(Number(seconds));

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;

  return `${hours || 0}h ${minutes || 0}m ${secs || 0}s`;
}


// Helper function to convert color to darker version
export const getDarkerColor = (color: string): string => {
    // Handle hex colors
    if (color.startsWith('#')) {
        const hex = color.slice(1);
        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);
        
        // Make it darker by reducing each component by 40%
        const darkerR = Math.floor(r * 0.2);
        const darkerG = Math.floor(g * 0.2);
        const darkerB = Math.floor(b * 0.2);
        
        return `rgb(${darkerR}, ${darkerG}, ${darkerB})`;
    }
    
    // Handle rgb/rgba colors
    if (color.startsWith('rgb')) {
        const rgbMatch = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
        if (rgbMatch) {
            const r = parseInt(rgbMatch[1]);
            const g = parseInt(rgbMatch[2]);
            const b = parseInt(rgbMatch[3]);
            
            // Make it darker by reducing each component by 40%
            const darkerR = Math.floor(r * 0.2);
            const darkerG = Math.floor(g * 0.2);
            const darkerB = Math.floor(b * 0.2);
            
            return `rgb(${darkerR}, ${darkerG}, ${darkerB})`;
        }
    }
    
    // Fallback for other color formats
    return '#111';
};

/**
 * Sets up listeners to keep React theme state in sync with localStorage and custom theme events.
 * Usage in a component:
 *   useEffect(() => setupLivisThemeSync(setTheme), []);
 * @param setTheme - React state setter for theme
 * @returns cleanup function to remove listeners
 */
export const setupLivisThemeSync = (setTheme: (theme: string) => void) => {
  const handleStorage = (e: StorageEvent) => {
    if (e.key === 'livis_theme') {
      setTheme(e.newValue || 'light');
    }
  };

  const handleCustomThemeChange = (e: Event) => {
    const newTheme = (e as CustomEvent<string>).detail;
    setTheme(newTheme || getLivistheme());
  };

  window.addEventListener('storage', handleStorage);
  window.addEventListener('livis-theme-change', handleCustomThemeChange as EventListener);

  return () => {
    window.removeEventListener('storage', handleStorage);
    window.removeEventListener('livis-theme-change', handleCustomThemeChange as EventListener);
  };
};

/**
 * Helper function to convert storage values with units to bytes
 * Handles B, KB, MB, GB, TB units
 */
export const parseStorageToBytes = (value: string, unit?: string): number => {
  if (!unit) return 0; // No unit means it's a count, not storage

  const k = 1024;
  const unitMap: { [key: string]: number } = {
    'B': 1,
    'KB': k,
    'MB': k * k,
    'GB': k * k * k,
    'TB': k * k * k * k
  };

  const num = parseFloat(value.replace(/,/g, ''));
  const unitUpper = unit.toUpperCase();
  const multiplier = unitMap[unitUpper] || 1;
  return num * multiplier;
};

/**
 * Calculates MD5 hash of a file using spark-md5 for memory efficiency.
 * Processes file in chunks to avoid loading entire file into memory.
 * @param file - File to hash
 * @returns Promise resolving to hex string hash
 */
export const calculateFileHash = async (file: File): Promise<string> => {
  const SparkMD5 = (await import('spark-md5')).default;
  const HASH_CHUNK_SIZE = 5 * 1024 * 1024; // 5MB chunks for hashing
  const spark = new SparkMD5.ArrayBuffer();
  const fileSize = file.size;
  let offset = 0;
  
  // Process file in chunks
  while (offset < fileSize) {
    const chunk = file.slice(offset, offset + HASH_CHUNK_SIZE);
    const buffer = await chunk.arrayBuffer();
    spark.append(buffer);
    offset += HASH_CHUNK_SIZE;
  }
  
  return spark.end();
};

/**
 * Calculates total number of chunks for a file.
 * @param file - File to chunk
 * @param chunkSize - Size of each chunk in bytes (default 1MB)
 * @returns Total number of chunks
 */
export const getTotalChunks = (file: File, chunkSize: number = 1024 * 1024): number => {
  return Math.ceil(file.size / chunkSize);
};

/**
 * Extracts a specific chunk from a file.
 * @param file - File to extract chunk from
 * @param chunkNumber - 0-indexed chunk number
 * @param chunkSize - Size of each chunk in bytes (default 1MB)
 * @returns Blob containing the chunk data
 */

// Chunk_0 starts with FF D8 FF → Detected as "JPEG image"
// Chunk_1-last don't have recognizable headers → Detected as "data"
// Chunk_last starts with FF D9 FF → Detected as "JPEG image"
export const getChunk = (file: File, chunkNumber: number, chunkSize: number = 1024 * 1024): Blob => {
  const start = chunkNumber * chunkSize;
  const end = Math.min(start + chunkSize, file.size);
  return file.slice(start, end);
};